import React from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { IoMdDownload } from "react-icons/io";

const NodeDownload = ({ environmentData, selectedEnvironment, selectedClusters, environmentTime }) => {
  const generatePDF = () => {
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();

    const drawCacheTime = () => {
      doc.setFontSize(10);
      const timeText = `Date and Time: ${environmentTime || 'None'}`;
      const timeWidth = doc.getStringUnitWidth(timeText) * 10 / doc.internal.scaleFactor;
      doc.text(timeText, pageWidth - timeWidth - 10, 10);
    };

    const wrapText = (text, maxWidth) => {
      const words = text.split(', ');
      let lines = [];
      let currentLine = words[0];

      for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const width = doc.getStringUnitWidth(currentLine + ', ' + word) * doc.internal.getFontSize() / doc.internal.scaleFactor;
        
        if (width < maxWidth) {
          currentLine += ', ' + word;
        } else {
          lines.push(currentLine);
          currentLine = word;
        }
      }
      lines.push(currentLine);
      return lines;
    };

    const drawFirstPageHeader = () => {
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      const title = 'Node Monitor Dashboard';
      const titleWidth = doc.getStringUnitWidth(title) * 16 / doc.internal.scaleFactor;
      const titleX = (pageWidth - titleWidth) / 2;
      doc.text(title, titleX, 20);

      doc.setFont(undefined, 'normal');
      doc.setFontSize(12);

      let yPosition = 35;

      // Environment
      if (selectedEnvironment) {
        doc.text(`Environment: ${selectedEnvironment}`, 14, yPosition);
        yPosition += 8;
      }

      // Clusters
      if (selectedClusters && selectedClusters.length > 0) {
        const maxWidth = pageWidth - 28; // Adjust based on page margins
        
        if (selectedClusters.includes('Select All')) {
          doc.text('Cluster: All Clusters', 14, yPosition);
          yPosition += 6;
          
          // Get all unique clusters from environmentData
          const allClusters = [...new Set(environmentData.map(item => item.cluster))].filter(Boolean).sort();
          
          if (allClusters.length > 0) {
            const wrappedClusters = wrapText(allClusters.join(', '), maxWidth);
            wrappedClusters.forEach(line => {
              doc.text(line, 14, yPosition);
              yPosition += 5;
            });
          }
          yPosition += 3;
        } else {
          doc.text(`Cluster: ${selectedClusters.join(', ')}`, 14, yPosition);
          yPosition += 8;
        }
      }

      return yPosition + 5;
    };

    let filteredData = environmentData || [];

    if (selectedClusters && selectedClusters.length > 0 && !selectedClusters.includes('Select All')) {
      filteredData = filteredData.filter(item => selectedClusters.includes(item.cluster));
    }

    // Convert CPU and memory values to standardized formats
    const normalizeResourceValue = (value) => {
      if (!value || value === 'N/A') return 'N/A';

      // Handle CPU values
      if (value.endsWith('m')) {
        return value;
      } else if (value.endsWith('n')) {
        return `${(parseInt(value.slice(0, -1)) / 1000000).toFixed(0)}m`;
      }

      // Handle numeric CPU values (convert to millicores format)
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && !value.includes("B") && !value.includes("i")) {
        return `${Math.round(numValue * 1000)}m`;
      }

      // Handle memory values
      if (value.endsWith('Ki')) {
        return `${Math.round(parseInt(value.slice(0, -2)) / 1024)} MB`;
      } else if (value.endsWith('Mi')) {
        return `${parseInt(value.slice(0, -2))} MB`;
      } else if (value.endsWith('Gi')) {
        return `${parseInt(value.slice(0, -2))} GB`;
      }

      // Return as is if no unit matches
      return value;
    };

    // Calculate percentage
    const calculatePercentage = (usage, limit) => {
      if (!usage || !limit || usage === 'N/A' || limit === 'N/A') return 'N/A';
      
      // Extract numeric values for calculation
      const getNumericValue = (val) => {
        if (val.endsWith('m')) {
          return parseInt(val.slice(0, -1)) / 1000;
        } else if (val.includes('MB')) {
          return parseInt(val.split(' ')[0]);
        } else if (val.includes('GB')) {
          return parseInt(val.split(' ')[0]) * 1024;
        }
        return parseFloat(val) || 0;
      };
      
      const usageVal = getNumericValue(usage);
      const limitVal = getNumericValue(limit);
      
      if (usageVal === 0 || limitVal === 0) return 'N/A';
      return ((usageVal / limitVal) * 100).toFixed(1);
    };

    // Process node data for the table
    const data = filteredData.map(node => {
      const cpuLimit = normalizeResourceValue(node.allocatable?.cpu || 'N/A');
      const cpuUsage = normalizeResourceValue(node.usage?.cpu || 'N/A');
      const cpuPercent = node.allocatable?.cpu && node.usage?.cpu ? 
        calculatePercentage(cpuUsage, cpuLimit) : 'N/A';
      
      const memoryLimit = normalizeResourceValue(node.allocatable?.memory || 'N/A');
      const memoryUsage = normalizeResourceValue(node.usage?.memory || 'N/A');
      const memoryPercent = node.allocatable?.memory && node.usage?.memory ? 
        calculatePercentage(memoryUsage, memoryLimit) : 'N/A';
      
      return {
        clusterName: node.cluster || '-',
        nodeName: node.name || '-',
        cpuUsage: cpuUsage || '-',
        cpuLimit: cpuLimit || '-',
        cpuPercent: cpuPercent === 'N/A' ? '-' : `${cpuPercent}%`,
        memoryUsage: memoryUsage || '-',
        memoryLimit: memoryLimit || '-',
        memoryPercent: memoryPercent === 'N/A' ? '-' : `${memoryPercent}%`,
        // Add numeric values for conditional formatting
        cpuPercentValue: cpuPercent === 'N/A' ? -1 : parseFloat(cpuPercent),
        memoryPercentValue: memoryPercent === 'N/A' ? -1 : parseFloat(memoryPercent)
      };
    });

    const columns = [
      { header: 'Cluster Name', dataKey: 'clusterName' },
      { header: 'Node Name', dataKey: 'nodeName' },
      { header: 'CPU Usage', dataKey: 'cpuUsage' },
      { header: 'CPU Limit', dataKey: 'cpuLimit' },
      { header: 'CPU %', dataKey: 'cpuPercent' },
      { header: 'Memory Usage', dataKey: 'memoryUsage' },
      { header: 'Memory Limit', dataKey: 'memoryLimit' },
      { header: 'Memory %', dataKey: 'memoryPercent' }
    ];

    drawCacheTime();
    const startY = drawFirstPageHeader();

    if (data.length === 0) {
      doc.setFontSize(12);
      doc.text("No data available", 14, startY + 10);
    } else {
      doc.autoTable({
        columns: columns,
        body: data,
        startY: startY,
        styles: {
          fontSize: 8,
          cellPadding: 3,
          lineWidth: 0.1,
          lineColor: [80, 80, 80]
        },
        columnStyles: {
          clusterName: { cellWidth: 35 },
          nodeName: { cellWidth: 45, overflow: 'linebreak' },
          cpuUsage: { cellWidth: 25 },
          cpuLimit: { cellWidth: 25 },
          cpuPercent: { cellWidth: 20 },
          memoryUsage: { cellWidth: 30 },
          memoryLimit: { cellWidth: 30 },
          memoryPercent: { cellWidth: 20 }
        },
        headStyles: {
          fillColor: [20, 66, 66],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'left'
        },
        bodyStyles: {
          halign: 'left'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        // Custom cell styling for high CPU and memory percentages
        didDrawCell: (data) => {
          // Check if we're dealing with CPU % or Memory % columns
          if (data.column.dataKey === 'cpuPercent' || data.column.dataKey === 'memoryPercent') {
            const row = data.row.raw;
            const percentValue = data.column.dataKey === 'cpuPercent' 
              ? row.cpuPercentValue 
              : row.memoryPercentValue;
            
            if (percentValue >= 80) {
              doc.setFillColor(231, 133, 135);
              doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
              
              // Reset to default fill color
              doc.setFillColor(255, 255, 255);
              
              // Draw the text in white for better contrast
              const value = data.column.dataKey === 'cpuPercent' ? row.cpuPercent : row.memoryPercent;
              doc.setTextColor(255, 255, 255);
              doc.text(value, data.cell.x + data.cell.padding('left'), data.cell.y + data.cell.padding('top') + data.cell.styles.fontSize / 3);
              doc.setTextColor(0, 0, 0); // Reset text color
            }
          }
        },
        margin: { top: 25, left: 10, right: 10 },
        rowPageBreak: 'avoid',
        showFoot: 'lastPage',
        didDrawPage: (data) => {
          const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
          const totalPages = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.text(`Page ${pageNumber} of ${totalPages}`, data.settings.margin.left, doc.internal.pageSize.height - 10);
          drawCacheTime();
        }
      });
    }

    const timestamp = new Date().toISOString().split('T')[0]; 
    const environmentName = selectedEnvironment ? selectedEnvironment.replace(/\s+/g, '_') : 'environment';
    const fileName = `node_monitor_${environmentName}_${timestamp}.pdf`;

    doc.save(fileName);
  };

  return (
    <button 
      className="download-button"
      onClick={generatePDF}
      disabled={!environmentData || environmentData.length === 0}
      data-tooltip="Download Node Data"
    >
      <IoMdDownload />
    </button>
  );
};

export default NodeDownload;